import _ from 'lodash';
import { ImsObject } from 'app/shared/collections/ims_object';
import {
    HINT_JOB_FAIL,
    HINT_JOB_SUCCESS,
    JobType,
    StoreAndForwardChangeEvent,
    StoreAndForwardJob,
    StoreAndForwardJobStore,
} from 'app/shared/store_and_forward/StoreAndForwardJobStore';
import { PromisedTimeout } from 'app/shared/utilities/PromisedTimeout';
import { ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import ImsObjectCollection from 'app/shared/collections/ims_object_collection.service';
import { IMS_TYPE__ASSET } from 'app/shared/collections/asset';

type RequestLocalIdCallback = () => number;

/**
 * Returns on either a successful request or a successfully queuing a request.
 */
export interface CRUDRunnerResult {
    jobType: JobType;
    resultCode: CRUDRunnerResultCode;
    resource: any;
}

/**
 * An error has occured during store and forward. The local object has had a conflict
 * with an object in the IMS API
 */
export class StoreAndForwardCRUDConflictError extends Error {
    public imsItemType: string;
    public localObjId: number;
    public apiObjId: number;

    constructor(imsItemType: string, localObjId: number, apiObjId: number) {
        super(`Conflict for ${imsItemType}. Local obj id ${localObjId}, API obj id ${apiObjId}`);
        this.imsItemType = imsItemType;
        this.localObjId = localObjId;
        this.apiObjId = apiObjId;
    }
}

export enum CRUDRunnerResultCode {
    REQUEST_COMPLETE = 'crud_runner_request_complete',
    REQUEST_QUEUED = 'crud_runner_request_queued',
}

export interface CollectionCRUDRunner {
    addObject(
        obj: ImsObject,
        requestLocalIdCallback: RequestLocalIdCallback,
    ): Promise<CRUDRunnerResult>;
    updateObject(origObj: ImsObject, newObj: ImsObject): Promise<CRUDRunnerResult>;
    removeObject(obj: ImsObject): Promise<CRUDRunnerResult>;
    hasAddJobInQueue(obj: ImsObject): boolean;
}

const TIMEOUT_BEFORE_QUEUEING_MS = 3000;

interface StoreAndForwardCRUDRunnerParams {
    jobStore: StoreAndForwardJobStore;
    imsItemTypeRegistry: ImsItemTypeRegistry;
    // Inject the navigator for testability
    navigator: Navigator;
    timeoutMs?: number;
}

/**
 * This is really a CUDRunner but....that'd just be confusing.
 *
 * A strange class.
 * It provides CRUD methods that abstract away calls to angular $resource
 * objects. This object always returns promises.
 *
 * This object has 2 types of clients.
 *
 * 1. Collections:
 *    Collections call into this class to execute CRUD operations against the API.
 *    This class immediately adds a job to the StoreAndForwarder. The StoreAndForwarder
 *    immediately attempts to execute the job if the newly added job is at the head
 *    of the queue. Meanwhile, the xxxObject() method
 *    returns a Promise that will either resolve with the StoreAndForwardJob completing
 *    within a timeout, or, a notification that the job has been queued.
 *
 * 2. StoreAndForwarder
 *    StoreAndForwarder uses the runJob() method to actually run jobs which results
 *    in calls being made to the API.
 *
 */
export class StoreAndForwardCRUDRunner implements CollectionCRUDRunner {
    private _jobStore: StoreAndForwardJobStore;
    private _timeoutMs: number;
    private _imsItemTypeRegistry: ImsItemTypeRegistry;
    private _jobsWaitingForAResponseByUuid;
    private _navigator: Navigator;

    constructor(params: StoreAndForwardCRUDRunnerParams) {
        params = Object.assign(
            {},
            {
                timeoutMs: TIMEOUT_BEFORE_QUEUEING_MS,
            },
            params,
        );
        if (!params.imsItemTypeRegistry) {
            throw new Error('params.imsItemTypeRegistry must be truthy');
        }
        if (!params.jobStore) {
            throw new Error(`params.jobStore must be truthy`);
        }
        if (!params.navigator) {
            throw new Error(`params.navigator must be truthy`);
        }
        this._navigator = params.navigator;
        this._imsItemTypeRegistry = params.imsItemTypeRegistry;
        this._timeoutMs = params.timeoutMs;
        this._jobStore = params.jobStore;
        this._jobsWaitingForAResponseByUuid = {};
    }

    /**
     *
     * @param {ImsObject} obj object to add
     * @param requestLocalIdCallback We need to provide a temporary ID, callback into the collection with this function
     * @returns {Promise<any>} An angularjs resource object
     */
    async addObject(
        obj: ImsObject,
        requestLocalIdCallback: RequestLocalIdCallback,
    ): Promise<CRUDRunnerResult> {
        obj.resource.id = requestLocalIdCallback();
        const newJob = this._jobStore.addJob({
            jobType: JobType.ADD,
            imsItemType: obj.imsType,
            objResource: obj.resource.toJSON(),
        });
        return this.handleInitialJobRun(newJob, obj);
    }

    /**
     * updateObject() needs the orig and the newObj so we can run a diff on the 2 objects
     *
     * @param {ImsObject} oldObj original version of the object
     * @param {ImsObject} newObj edited version of the object
     * @returns {Promise<CRUDRunnerResult>} An angularjs resource object
     */
    async updateObject(oldObj: ImsObject, newObj: ImsObject): Promise<CRUDRunnerResult> {
        // sanity checks
        if (oldObj && !oldObj.resource.toJSON) {
            throw new Error('no oldResource.toJSON()');
        }
        if (newObj && !newObj.resource.toJSON) {
            throw new Error('no newResource.toJSON()');
        }

        const defaultResource = {
            toJSON: () => ({}),
        };

        const oldResource = oldObj ? oldObj.resource : defaultResource;
        const newResource = newObj ? newObj.resource : defaultResource;

        const newJob = this._jobStore.addJob({
            jobType: JobType.UPDATE,
            imsItemType: newObj.imsType,
            // There are cases when there is no oldObj, e.g. un-deleting
            oldObjResource: oldResource.toJSON(),
            objResource: newResource.toJSON(),
        });
        return this.handleInitialJobRun(newJob, newObj);
    }

    /**
     *
     * @param {ImsObject} obj object to remove
     * @returns {Promise<CRUDRunnerResult>} An angularjs resource object
     */
    async removeObject(obj: ImsObject): Promise<CRUDRunnerResult> {
        const newJob = this._jobStore.addJob({
            jobType: JobType.REMOVE,
            imsItemType: obj.imsType,
            objResource: obj.resource.toJSON(),
        });
        return this.handleInitialJobRun(newJob, obj);
    }

    /**
     * Exposing information about the job store through this crud runner
     * to reduce the number of outside dependencies required for the
     * ims object collection
     *
     * @param {ImsObject} obj
     * @returns {boolean}
     */
    hasAddJobInQueue(obj: ImsObject): boolean {
        if (obj.id > 0) {
            return false;
        } else {
            return this._jobStore.hasAddJobInQueue(obj.imsType, obj.id);
        }
    }

    /**
     * A re-entry point into CRUDRunner. Used by the store and forward scheduler to
     * actually run the job.
     *
     * When the navigator shows the device as offline, all job
     * promises will reject immediately.
     *
     * @param {StoreAndForwardJob} job
     * @returns {Promise<CRUDRunnerResult>}
     */
    async runJob(job: StoreAndForwardJob) {
        if (!this._navigator.onLine) {
            const err: any = new Error(`Device is offline`);
            // Same status as a timeout
            err.status = -1;
            // Pretend to be similar to an API error
            err.data = {
                detail: `Device is offline`,
            };
            return Promise.reject(err);
        }

        const collection = this.getCollection(job.imsItemType);
        const funcName = StoreAndForwardCRUDRunner.getResourceFuncName(job);
        const preRequestResource = _.cloneDeep(job.objResource);
        const resourceToSubmit = this.getResourceToSubmit(job);

        let resultResource;

        try {
            resultResource =
                await collection.resourceObjectBase[funcName](resourceToSubmit).$promise;
        } catch (err) {
            if (job.imsItemType === IMS_TYPE__ASSET) {
                // we may have had a duplicate name conflict that needs
                // to be resolved.
                // keeping this behaviour very narrow for now on purpose because
                // it's only useful for mobile store and forward and there are very
                // few users (1 at the time of writing) of that use case
                const conflictingAssetIds = err?.data?.conflicting_asset_id;
                if (conflictingAssetIds) {
                    if (conflictingAssetIds.length === 1) {
                        const objResourceId = job.objResource.id;
                        // We need to delete the temporary asset in question
                        // from the local collection
                        const tempObject = collection.all[objResourceId];
                        if (tempObject) {
                            await collection.removeObjectOffline(tempObject);
                        }
                        // Inform the next layer up about the conflict
                        throw new StoreAndForwardCRUDConflictError(
                            job.imsItemType,
                            objResourceId,
                            conflictingAssetIds[0],
                        );
                    } else {
                        // Shouldn't be possible? Just rethrow a little further down
                        console.error(
                            'Expected a single conflicting asset ID for duplicate name but got:',
                            err.data,
                        );
                    }
                }
            }
            // It's some other error we don't know about, re-throw!
            throw err;
        }

        const crudResult = buildCRUDRunnerResult(
            CRUDRunnerResultCode.REQUEST_COMPLETE,
            job.jobType,
            resultResource,
        );

        // This if statement evaluates to truthy when the initial function call to
        // xxxObject has timed out and the job has been 'disconnected' from the original UI
        // action
        if (!this._jobsWaitingForAResponseByUuid[job.uuid]) {
            // For this hint type, event data is a $resource object
            const collection: ImsObjectCollection = this.getCollection(job.imsItemType);
            // This imsObj should be representative of the ims object just before the
            // request took place
            const imsObj = collection.createImsObject(preRequestResource);
            await collection.processCrudResponse(
                Promise.resolve(crudResult),
                imsObj,
                preRequestResource,
                job.jobType === JobType.REMOVE ? true : undefined,
            );
        }
        return crudResult;
    }

    /**
     * After a job is added by this CRUDRunner, this function allows the public
     * CRUD functions to return a promise that will...
     * - resolves with the job done if the request completes within a timeout
     * - resolves with the the job being queued if a response is still being waited for after
     *   the timeout passes
     * - rejects with the standard error response if the API responds with a bad error code
     *   within the timeout.
     *
     * @param {StoreAndForwardJob} job
     * @param {ImsObject} sentImsObj
     * @returns {Promise<CRUDRunnerResult>}
     */
    private async handleInitialJobRun(
        job: StoreAndForwardJob,
        sentImsObj: ImsObject,
    ): Promise<CRUDRunnerResult> {
        if (!sentImsObj.resource.toJSON) {
            throw new Error('no sentImsObj.resource.toJSON()');
        }

        try {
            this._jobsWaitingForAResponseByUuid[job.uuid] = true;
            let promises: Promise<any>[];
            if (this._navigator.onLine) {
                // If we are online use the normal timeout and attempt
                // to wait for the job
                promises = [
                    new PromisedTimeout(this._timeoutMs, {
                        hint: PromisedTimeout.TIMEOUT,
                    }).promise,
                    this._jobStore.getJobPromise(job),
                ];
            } else {
                // we are offline, return immediately with timeout
                promises = [
                    Promise.resolve({
                        hint: PromisedTimeout.TIMEOUT,
                    }),
                ];
            }
            const result: StoreAndForwardChangeEvent = await Promise.race(promises);
            const eventJob = result.job;
            if (result.hint === PromisedTimeout.TIMEOUT) {
                return buildCRUDRunnerResult(
                    CRUDRunnerResultCode.REQUEST_QUEUED,
                    job.jobType,
                    sentImsObj.resource,
                );
            } else if (result.hint === HINT_JOB_SUCCESS) {
                // Ignore this result as this is where 'proxy' objects are returned
                // while the StoreAndForwardJobRunner is actually running something.
                // The JobRunner will call processCrudResult in order to get the
                // real result into the collection.
                const resource = result.eventData;
                if (!resource.toJSON) {
                    throw new Error(`no resource.toJSON`);
                }
                return buildCRUDRunnerResult(
                    CRUDRunnerResultCode.REQUEST_COMPLETE,
                    eventJob.jobType,
                    resource,
                );
            } else {
                throw new Error('not handled');
            }
        } catch (err) {
            if (err.hint === HINT_JOB_FAIL) {
                // Event data will be a JS error object
                const jobFailErr = err.eventData;
                if (jobFailErr.status === -1) {
                    if (!sentImsObj.resource.toJSON) {
                        throw new Error('no sentImsObj.resource.toJSON()');
                    }
                    // timeout or conn refused
                    return buildCRUDRunnerResult(
                        CRUDRunnerResultCode.REQUEST_QUEUED,
                        job.jobType,
                        sentImsObj.resource,
                    );
                } else if (jobFailErr.status === 400) {
                    // some kind of error...
                    this._jobStore.cancelJob(job);
                    return Promise.reject(jobFailErr);
                }
            }
            // Something else has happened...
            console.error(err);
            return Promise.reject(err);
        } finally {
            delete this._jobsWaitingForAResponseByUuid[job.uuid];
        }
    }

    private getCollection(imsItemType: string): ImsObjectCollection {
        const typeDetails = this._imsItemTypeRegistry.getTypeDetails(imsItemType);
        if (!typeDetails) {
            throw new Error(`No type details found for ${imsItemType}`);
        }
        if (!typeDetails.collection) {
            throw new Error(`No collection found for ${imsItemType}`);
        }
        return typeDetails.collection;
    }

    /**
     * Just a helper. Pick the write resource function to call
     * for a given job type.
     * @param {StoreAndForwardJob} job
     * @returns {any}
     */
    private static getResourceFuncName(job: StoreAndForwardJob) {
        if (job.jobType === JobType.ADD) {
            return 'save';
        } else if (job.jobType === JobType.UPDATE) {
            return 'update';
        } else if (job.jobType === JobType.REMOVE) {
            return 'remove';
        } else {
            throw new Error(`job type not supported ${job.jobType}`);
        }
    }

    /**
     * Just a helper
     * Clean up the resource object and do sanity checks
     * @param {StoreAndForwardJob} job
     */
    private getResourceToSubmit(job: StoreAndForwardJob) {
        const collection = this.getCollection(job.imsItemType);
        let strippedResource = Object.assign({}, job.objResource);
        if (job.jobType === JobType.REMOVE) {
            // if we're removing the resource, don't send anything but the ID
            // otherwise the API might try and validate fields which we don't care about
            strippedResource = { id: job.objResource.id };
        }
        if (job.jobType === JobType.ADD) {
            delete strippedResource.id;
        } else {
            if (strippedResource.id < 0) {
                throw new Error(
                    `Attempting to submit resource with invalid id ${strippedResource.id}`,
                );
            }
        }
        // Create an actual angular resource object
        return new collection.resourceObjectBase(strippedResource);
    }
}

/**
 * The default crud runner used by collections that don't have the 'real' crud runner
 * injected at construction and preserves the original, submit immediately, behaviour.
 *
 * This avoids a crud runner everywhere while prototyping.
 * At some point in the future we might want all collections to use the
 * more clever crud runner.
 */
export class SimpleCRUDRunner implements CollectionCRUDRunner {
    async addObject(
        obj: ImsObject,
        requestLocalIdCallback: RequestLocalIdCallback,
    ): Promise<CRUDRunnerResult> {
        const resource = await obj.resource.$save();
        return buildCRUDRunnerResult(CRUDRunnerResultCode.REQUEST_COMPLETE, JobType.ADD, resource);
    }

    async updateObject(origObj: ImsObject, newObj: ImsObject): Promise<CRUDRunnerResult> {
        const resource = await newObj.resource.$update();
        return buildCRUDRunnerResult(
            CRUDRunnerResultCode.REQUEST_COMPLETE,
            JobType.UPDATE,
            resource,
        );
    }

    async removeObject(obj: ImsObject): Promise<CRUDRunnerResult> {
        const resource = await obj.resource.$remove();
        return buildCRUDRunnerResult(
            CRUDRunnerResultCode.REQUEST_COMPLETE,
            JobType.REMOVE,
            resource,
        );
    }

    /**
     * Simple crud runner has no queue
     * @param {ImsObject} obj
     * @returns {boolean}
     */
    hasAddJobInQueue(obj: ImsObject): boolean {
        return false;
    }
}

export function buildCRUDRunnerResult(
    resultCode: CRUDRunnerResultCode,
    jobType: JobType,
    resource: any,
): CRUDRunnerResult {
    if (!resource) {
        throw new Error(`resource is undefined!`);
    }
    return {
        jobType: jobType,
        resultCode: resultCode,
        resource: resource,
    };
}
